import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowRight,
  Circle,
  FileCode,
  FileText,
  PlayCircle,
  CheckCircle,
  XCircle,
  Stack,
  DownloadSimple,
  Clock,
  ArrowsClockwise,
  TerminalWindow,
  SpinnerGap
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DASHBOARD } from '@/constants/testIds';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';
import apiClient from '@/api/client';
import { listFiles, listFileVersions, downloadFileVersion } from '@/api/projects';

const DEFAULT_PYTHON_SOURCE = `print("VASTEON-M12-API-E2E-OK")`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const InteroperabilityWorkspace = ({
  session,
  project: propProject,
  messages,
  onRunSandbox,
  onApproveResult,
  onArchiveProject,
  exporting = false,
  initialTab = 'preview',
  activeTab: propActiveTab,
  onTabChange
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(initialTab);
  const activeTab = propActiveTab !== undefined ? propActiveTab : internalActiveTab;

  const handleTabChange = useCallback((nextTab) => {
    setInternalActiveTab(nextTab);
    if (typeof onTabChange === 'function') {
      onTabChange(nextTab);
    }
  }, [onTabChange]);
  const [sourceCode, setSourceCode] = useState(DEFAULT_PYTHON_SOURCE);
  const [timeoutSeconds, setTimeoutSeconds] = useState(10);
  const [running, setRunning] = useState(false);
  const [activeExecution, setActiveExecution] = useState(null);
  const [projectExecutions, setProjectExecutions] = useState([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [executionsError, setExecutionsError] = useState(null);
  const [expandedExecutionIds, setExpandedExecutionIds] = useState(new Set());
  const [pollingError, setPollingError] = useState(null);

  // Real Project Files State (Mission 13-B1)
  const [realFiles, setRealFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);

  const originMessages = Array.isArray(messages)
    ? messages.filter((m) => m.destination === 'production_ai' || m.sender === 'origin' || m.message_type === 'instruction' || m.message_type === 'handoff')
    : [];
  const project = propProject || session?.project;

  const toggleExecutionExpanded = (id) => {
    setExpandedExecutionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const loadProjectExecutions = useCallback(async (projectId) => {
    if (!projectId) {
      setProjectExecutions([]);
      setExecutionsError(null);
      return;
    }
    try {
      setLoadingExecutions(true);
      setExecutionsError(null);
      const res = await apiClient.get(`/sandbox/${projectId}/executions`);
      const execs = res.data || [];
      setProjectExecutions(Array.isArray(execs) ? execs : []);
      if (execs.length > 0 && !activeExecution) {
        setActiveExecution(execs[0]);
      }
    } catch (err) {
      setProjectExecutions([]);
      const errDetail =
        err.response?.status === 401
          ? 'Sesión no válida o no autenticada.'
          : err.response?.status === 404
          ? 'Proyecto no disponible.'
          : 'Error al consultar ejecuciones del proyecto.';
      setExecutionsError(errDetail);
    } finally {
      setLoadingExecutions(false);
    }
  }, [activeExecution]);

  const loadProjectFiles = useCallback(async (projectId) => {
    if (!projectId) {
      setRealFiles([]);
      setFilesError(null);
      return;
    }
    try {
      setLoadingFiles(true);
      setFilesError(null);
      const data = await listFiles(projectId);
      setRealFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      setRealFiles([]);
      const errDetail =
        err.response?.status === 401
          ? 'Sesión no válida o no autenticada.'
          : err.response?.status === 404
          ? 'Proyecto no disponible.'
          : 'Error al consultar archivos del proyecto.';
      setFilesError(errDetail);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    const projectId = project?.id || project?.project_id;
    if (projectId) {
      loadProjectExecutions(projectId);
      loadProjectFiles(projectId);
    } else {
      setProjectExecutions([]);
      setExecutionsError(null);
      setActiveExecution(null);
      setRealFiles([]);
      setFilesError(null);
    }
  }, [project?.id, project?.project_id, loadProjectExecutions, loadProjectFiles]);

  const handleDownloadFile = async (file) => {
    const projectId = project?.id || project?.project_id;
    if (!projectId || !file?.file_id) return;

    try {
      setDownloadingFileId(file.file_id);
      const versions = await listFileVersions(projectId, file.file_id);
      if (!versions || versions.length === 0) {
        toast.info('El archivo no tiene versiones disponibles para descargar.');
        return;
      }
      const latestVersion = versions[versions.length - 1];
      const res = await downloadFileVersion(projectId, file.file_id, latestVersion.version_id);

      const blob = new Blob([res.data], { type: file.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename || 'download');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al descargar el archivo');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleExecuteSandbox = async () => {
    const projectId = project?.id || project?.project_id;
    if (!projectId) return;

    try {
      setRunning(true);
      setPollingError(null);

      const timeoutVal = Math.min(Math.max(Number(timeoutSeconds) || 10, 1), 30);
      const postRes = await apiClient.post(`/sandbox/${projectId}/run`, {
        runtime: 'python',
        source: sourceCode || DEFAULT_PYTHON_SOURCE,
        args: [],
        timeout_seconds: timeoutVal
      });

      const { execution_id, status } = postRes.data;
      const initialExec = {
        execution_id,
        project_id: projectId,
        status: status || 'queued',
        runtime: 'python',
        created_at: new Date().toISOString()
      };
      setActiveExecution(initialExec);

      // Start limited polling
      let attempts = 0;
      const maxAttempts = 30; // 30 * 600ms = 18s
      const pollInterval = 600;

      const poll = async () => {
        attempts += 1;
        try {
          const getRes = await apiClient.get(`/sandbox/executions/${execution_id}`);
          const execData = getRes.data;
          setActiveExecution(execData);

          if (['completed', 'failed', 'timed_out'].includes(execData.status)) {
            setRunning(false);
            loadProjectExecutions(projectId);
            if (onRunSandbox) {
              onRunSandbox(execData);
            }
            return;
          }

          if (attempts >= maxAttempts) {
            setRunning(false);
            setPollingError('Tiempo de espera de sondeo agotado. Consulta el historial de ejecuciones.');
            loadProjectExecutions(projectId);
            return;
          }

          setTimeout(poll, pollInterval);
        } catch (err) {
          setRunning(false);
          const errDetail =
            err.response?.status === 401
              ? 'Sesión no autenticada'
              : err.response?.status === 404
              ? 'Ejecución no encontrada'
              : 'Error al consultar estado de la ejecución';
          setPollingError(errDetail);
        }
      };

      setTimeout(poll, pollInterval);
    } catch (err) {
      setRunning(false);
      const errDetail =
        err.response?.status === 401
          ? 'Sesión no autenticada'
          : err.response?.status === 404
          ? 'Proyecto no encontrado'
          : err.response?.status === 422
          ? 'Parámetros de ejecución inválidos'
          : 'Error al despachar ejecución a Sandbox';
      setPollingError(errDetail);
    }
  };

  const translateStatus = (status) => {
    switch (status) {
      case 'queued':
        return { label: 'En cola', color: 'text-[#9CA3AF]', border: 'border-[#1F2937]', bg: 'bg-[#13131A]' };
      case 'running':
        return { label: 'Ejecutando', color: 'text-[#00E5FF]', border: 'border-[#00E5FF]/40', bg: 'bg-[#00E5FF]/10' };
      case 'completed':
        return { label: 'Completada', color: 'text-[#10B981]', border: 'border-[#10B981]/40', bg: 'bg-[#10B981]/10' };
      case 'failed':
        return { label: 'Fallida', color: 'text-[#F85149]', border: 'border-[#F85149]/40', bg: 'bg-[#F85149]/10' };
      case 'timed_out':
        return { label: 'Tiempo agotado', color: 'text-[#D97706]', border: 'border-[#D97706]/40', bg: 'bg-[#D97706]/10' };
      default:
        return { label: status || 'Desconocido', color: 'text-[#9CA3AF]', border: 'border-[#1F2937]', bg: 'bg-[#13131A]' };
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span
            data-testid={DASHBOARD.sandboxStatus}
            className="px-2.5 py-1 text-xs font-mono font-bold bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40 flex items-center gap-1.5"
          >
            <CheckCircle size={14} weight="fill" /> COMPLETED
          </span>
        );
      case 'failed':
        return (
          <span
            data-testid={DASHBOARD.sandboxStatus}
            className="px-2.5 py-1 text-xs font-mono font-bold bg-[#F85149]/15 text-[#F85149] border border-[#F85149]/40 flex items-center gap-1.5"
          >
            <XCircle size={14} weight="fill" /> FAILED
          </span>
        );
      case 'timed_out':
        return (
          <span
            data-testid={DASHBOARD.sandboxStatus}
            className="px-2.5 py-1 text-xs font-mono font-bold bg-[#D97706]/15 text-[#D97706] border border-[#D97706]/40 flex items-center gap-1.5"
          >
            <Clock size={14} weight="fill" /> TIMED_OUT
          </span>
        );
      case 'running':
        return (
          <span
            data-testid={DASHBOARD.sandboxStatus}
            className="px-2.5 py-1 text-xs font-mono font-bold bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40 flex items-center gap-1.5 animate-pulse"
          >
            <ArrowsClockwise size={14} className="animate-spin" /> RUNNING
          </span>
        );
      case 'queued':
      default:
        return (
          <span
            data-testid={DASHBOARD.sandboxStatus}
            className="px-2.5 py-1 text-xs font-mono font-bold bg-[#13131A] text-[#9CA3AF] border border-[#1F2937] flex items-center gap-1.5"
          >
            <Clock size={14} /> QUEUED
          </span>
        );
    }
  };

  return (
    <div data-testid={DASHBOARD.workspaceCenter} className="h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Origin AI → Production AI Messages (Only when non-empty) */}
      {originMessages && originMessages.length > 0 && (
        <div
          data-testid={DASHBOARD.workspaceMessagesOrigin}
          className="shrink-0 max-h-20 border-b border-white/[0.08] bg-[#0B0D13] p-2 overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between mb-1 shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="text-xs uppercase tracking-wider text-slate-400 font-sans font-medium">Origin AI → Production AI</h3>
              <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} size="sm" />
            </div>
            <ArrowRight size={14} className="text-cyan-400" />
          </div>
          <ScrollArea className="flex-1 min-h-0">
            <div className="space-y-1">
              {originMessages.map((msg) => (
                <div key={msg.id} className="p-2 bg-[#131620] border border-white/[0.06] rounded-md text-xs font-sans text-slate-200">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{msg.message_type}</span>
                    {msg.approved_by_user && (
                      <div className="flex items-center gap-1">
                        <CheckCircle size={12} className="text-emerald-400" weight="fill" />
                        <span className="text-[10px] text-emerald-400 font-medium">Aprobado</span>
                      </div>
                    )}
                  </div>
                  <p className="leading-relaxed text-xs text-slate-300">{msg.content}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Active Product Viewer */}
      <div
        data-testid={DASHBOARD.workspaceActiveProduct}
        className="flex-1 min-h-0 flex flex-col bg-[#10131B] overflow-hidden"
      >
        <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Integrated Compact Workspace Toolbar */}
          <div className="border-b border-white/[0.08] px-3 py-1.5 shrink-0 bg-[#0E1117] flex items-center justify-between gap-2 flex-wrap">
            {/* Left: Project Identity & Phase */}
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="text-xs font-semibold font-sans text-slate-100 truncate max-w-[200px]" title={project?.name || 'Sin Proyecto Activo'}>
                {project?.name || 'Sin Proyecto Activo'}
              </span>
              <div className="flex items-center gap-1.5 text-xs font-sans text-slate-400">
                <span>Fase:</span>
                <span className="text-cyan-400 font-medium">{project?.current_phase || 'Listo'}</span>
              </div>
            </div>

            {/* Center: Tabs */}
            <TabsList className="bg-white/[0.03] border border-white/[0.06] h-auto p-0.5 gap-1 rounded-lg">
              <TabsTrigger value="preview" className="font-sans text-xs py-1 px-2.5 rounded-md text-slate-400 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white transition-all">Preview (Demo)</TabsTrigger>
              <TabsTrigger value="files" className="font-sans text-xs py-1 px-2.5 rounded-md text-slate-400 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white transition-all">Files (Real)</TabsTrigger>
              <TabsTrigger value="executions" className="font-sans text-xs py-1 px-2.5 rounded-md text-slate-400 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white transition-all">Ejecuciones (Real)</TabsTrigger>
              <TabsTrigger value="sandbox" className="font-sans text-xs py-1 px-2.5 rounded-md text-slate-400 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white transition-all">Sandbox (Real)</TabsTrigger>
            </TabsList>

            {/* Right: Export action */}
            <div className="flex items-center gap-2">
              {project && (
                <Button
                  data-testid={DASHBOARD.archiveBtn}
                  size="sm"
                  onClick={onArchiveProject}
                  disabled={exporting}
                  className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 font-sans text-xs font-medium h-7 px-2.5 rounded-md transition-all shadow-xs"
                  title="Exportar archivo ZIP real del proyecto"
                >
                  <DownloadSimple size={13} className="mr-1" />
                  {exporting ? 'Exportando...' : 'Export'}
                </Button>
              )}
              <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} size="sm" label="REAL EXPORT" />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden p-2 flex flex-col">
            <TabsContent value="preview" className="mt-0 h-full flex-1 min-h-0 flex flex-col">
              <div className="bg-[#131620] border border-white/[0.06] rounded-xl p-8 h-full flex-1 min-h-0 flex flex-col items-center justify-center">
                {project ? (
                  <div className="text-center space-y-3 max-w-md mx-auto">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mx-auto">
                      <FileCode size={24} />
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <h3 className="text-base font-semibold font-sans text-slate-100">
                        Vista Previa del Producto
                      </h3>
                      <OperationalStatusBadge status={OPERATIONAL_STATUS.DEMO} />
                    </div>
                    <p className="text-xs font-sans text-slate-400 leading-relaxed">
                      Los artefactos generados corresponden al modo de demostración. Puedes exportar un archivo ZIP real de los artefactos usando el botón de exportación.
                    </p>
                  </div>
                ) : (
                  <div className="text-center max-w-sm mx-auto">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-slate-400 mx-auto mb-3">
                      <FileCode size={24} />
                    </div>
                    <h3 className="text-base font-semibold font-sans text-slate-100 mb-1">
                      Sin Vista Previa Activa
                    </h3>
                    <p className="text-xs font-sans text-slate-400 leading-relaxed">
                      Inicia una demostración guiada o conecta tus proveedores de IA para iniciar el flujo de interoperabilidad.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="files" className="mt-0 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
              <ScrollArea className="h-full flex-1 min-h-0">
                <div className="space-y-3 pr-3">
                  <div className="flex items-center justify-between pb-2 border-b border-[#1F2937]/60">
                    <div>
                      <h3 className="text-xs uppercase tracking-wider text-[#9CA3AF] terminal-font font-bold">
                        Archivos del Proyecto
                      </h3>
                      <p className="text-[11px] text-[#6B7280] font-mono mt-0.5">
                        Almacenamiento persistente en GridFS (MongoDB)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const projectId = project?.id || project?.project_id;
                          if (projectId) loadProjectFiles(projectId);
                        }}
                        disabled={loadingFiles}
                        className="p-1 hover:bg-[#1C1C24] text-[#9CA3AF] hover:text-[#00E5FF] rounded transition-colors"
                        title="Recargar archivos del proyecto"
                      >
                        <ArrowsClockwise size={14} className={loadingFiles ? 'animate-spin text-[#00E5FF]' : ''} />
                      </button>
                      <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} size="sm" label="REAL / GRIDFS" />
                    </div>
                  </div>

                  {loadingFiles ? (
                    <div className="py-12 flex flex-col items-center justify-center gap-3 text-[#9CA3AF]">
                      <SpinnerGap size={24} className="animate-spin text-[#00E5FF]" />
                      <span className="text-xs font-mono">Cargando archivos del proyecto...</span>
                    </div>
                  ) : filesError ? (
                    <div className="p-4 bg-[#1C1014] border border-[#F85149]/30 rounded text-xs font-mono space-y-1">
                      <div className="flex items-center gap-2 font-bold text-[#F85149]">
                        <XCircle size={15} /> Error al consultar archivos
                      </div>
                      <p className="text-[#9CA3AF]">{filesError}</p>
                    </div>
                  ) : realFiles.length === 0 ? (
                    <div className="py-12 px-4 text-center space-y-2">
                      <FileCode size={36} className="mx-auto text-[#4B5563]" />
                      <p className="text-sm font-mono text-[#F3F4F6] font-medium">No hay archivos en este proyecto.</p>
                      <p className="text-xs text-[#9CA3AF] max-w-sm mx-auto">
                        Los artefactos persistidos en GridFS aparecerán aquí automáticamente.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {realFiles.map((file) => (
                        <div
                          key={file.file_id || file.filename}
                          className="p-3 bg-[#13131A] hover:bg-[#1C1C24] border border-[#1F2937] hover:border-[#00E5FF]/40 transition-all rounded flex items-center justify-between gap-3 group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-[#0A0C10] border border-[#1F2937] text-[#00E5FF] rounded shrink-0">
                              <FileCode size={18} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono font-bold text-[#F3F4F6] truncate">
                                  {file.filename}
                                </span>
                                <span className="text-[10px] text-[#10B981] px-1.5 py-0.2 border border-[#10B981]/30 bg-[#10B981]/10 rounded shrink-0 font-mono">
                                  PERSISTIDO
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-[11px] text-[#9CA3AF] font-mono mt-0.5">
                                <span>{file.mime_type || 'application/octet-stream'}</span>
                                {file.updated_at && (
                                  <span>Actualizado: {formatDate(file.updated_at)}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadFile(file)}
                              disabled={downloadingFileId === file.file_id}
                              className="h-7 px-2.5 text-xs text-[#00E5FF] hover:text-[#00E5FF] hover:bg-[#00E5FF]/10 font-mono border border-transparent hover:border-[#00E5FF]/30 transition-all"
                              title="Descargar archivo desde GridFS"
                            >
                              {downloadingFileId === file.file_id ? (
                                <SpinnerGap size={13} className="animate-spin mr-1" />
                              ) : (
                                <DownloadSimple size={13} className="mr-1" />
                              )}
                              Descargar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="executions" className="mt-0 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
              <ScrollArea className="h-full flex-1 min-h-0">
                <div className="space-y-3 pr-3">
                  <div className="flex items-center justify-between pb-2 border-b border-[#1F2937]/60">
                    <div>
                      <h3 className="text-xs uppercase tracking-wider text-[#9CA3AF] terminal-font font-bold">
                        Historial de Ejecuciones de Sandbox
                      </h3>
                      <p className="text-[11px] text-[#6B7280] font-mono mt-0.5">
                        Evidencia operacional de procesos aislados persistida en MongoDB
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const projectId = project?.id || project?.project_id;
                          if (projectId) loadProjectExecutions(projectId);
                        }}
                        disabled={loadingExecutions}
                        className="p-1 hover:bg-[#1C1C24] text-[#9CA3AF] hover:text-[#00E5FF] rounded transition-colors"
                        title="Recargar historial de ejecuciones"
                      >
                        <ArrowsClockwise size={14} className={loadingExecutions ? 'animate-spin text-[#00E5FF]' : ''} />
                      </button>
                      <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} size="sm" label="ISOLATED RUNS" />
                    </div>
                  </div>

                  {!project ? (
                    <div className="bg-[#1C1C24] border border-[#1F2937] p-8 text-center">
                      <TerminalWindow size={36} className="mx-auto mb-2 text-[#9CA3AF]/60" />
                      <p className="text-xs text-[#9CA3AF] body-font">
                        Selecciona un proyecto para ver sus ejecuciones.
                      </p>
                    </div>
                  ) : executionsError ? (
                    <div className="bg-[#1C1014] border border-[#F85149]/40 p-4 text-xs font-mono text-[#F85149] space-y-2">
                      <div className="flex items-center gap-2 font-bold">
                        <XCircle size={15} />
                        <span>Error al consultar ejecuciones</span>
                      </div>
                      <p className="text-[#9CA3AF]">{executionsError}</p>
                      <button
                        onClick={() => {
                          const projectId = project?.id || project?.project_id;
                          if (projectId) loadProjectExecutions(projectId);
                        }}
                        className="px-2.5 py-1 text-[11px] bg-[#1C1C24] hover:bg-[#252530] text-[#F3F4F6] border border-[#1F2937] rounded"
                      >
                        Reintentar
                      </button>
                    </div>
                  ) : loadingExecutions && projectExecutions.length === 0 ? (
                    <div className="bg-[#1C1C24] border border-[#1F2937] p-8 text-center space-y-2">
                      <SpinnerGap size={24} className="mx-auto text-[#00E5FF] animate-spin" />
                      <p className="text-xs text-[#9CA3AF] font-mono">
                        Cargando ejecuciones de sandbox...
                      </p>
                    </div>
                  ) : projectExecutions.length === 0 ? (
                    <div className="bg-[#1C1C24] border border-[#1F2937] p-8 text-center space-y-2">
                      <TerminalWindow size={36} className="mx-auto text-[#9CA3AF]/50" />
                      <p className="text-xs font-bold text-[#F3F4F6] font-mono">
                        No hay ejecuciones registradas todavía.
                      </p>
                      <p className="text-[11px] text-[#9CA3AF] body-font">
                        Ejecuta código desde la pestaña Sandbox para generar evidencia operacional.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {projectExecutions.map((exec) => {
                        const isExpanded = expandedExecutionIds.has(exec.execution_id);
                        const statusMeta = translateStatus(exec.status);
                        const shortId = exec.execution_id ? exec.execution_id.slice(0, 8) : '-';
                        const runtimeLabel = exec.runtime === 'python' ? 'Python 3.11' : exec.runtime || 'Python 3.11';
                        const exitLabel = exec.exit_code !== null && exec.exit_code !== undefined ? `Exit ${exec.exit_code}` : 'Exit -';
                        const durationLabel = exec.duration_ms !== null && exec.duration_ms !== undefined ? `${exec.duration_ms} ms` : '-';
                        const dateLabel = formatDate(exec.finished_at || exec.created_at);

                        return (
                          <div
                            key={exec.execution_id}
                            className="bg-[#1C1C24] border border-[#1F2937] hover:border-[#1F2937]/80 transition-colors"
                          >
                            <div className="p-3 flex items-center justify-between flex-wrap gap-2 text-xs font-mono">
                              <div className="flex items-center gap-2.5 flex-wrap">
                                <span
                                  className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-none ${statusMeta.bg} ${statusMeta.color} ${statusMeta.border}`}
                                >
                                  {statusMeta.label}
                                </span>
                                <span className="text-[#9CA3AF] text-[11px]" title={exec.execution_id}>
                                  ID: <span className="text-[#F3F4F6] font-bold">{shortId}</span>
                                </span>
                                <span className="text-[#6B7280]">|</span>
                                <span className="text-[#9CA3AF] text-[11px]">{runtimeLabel}</span>
                                <span className="text-[#6B7280]">|</span>
                                <span className="text-[#F3F4F6] font-bold text-[11px]">{exitLabel}</span>
                                <span className="text-[#6B7280]">|</span>
                                <span className="text-[#00E5FF] text-[11px]">{durationLabel}</span>
                              </div>

                              <div className="flex items-center gap-3">
                                {dateLabel && (
                                  <span className="text-[10px] text-[#6B7280] flex items-center gap-1">
                                    <Clock size={11} /> {dateLabel}
                                  </span>
                                )}
                                <button
                                  onClick={() => toggleExecutionExpanded(exec.execution_id)}
                                  className="text-[11px] text-[#00E5FF] hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
                                >
                                  {isExpanded ? '▾ Ocultar detalles' : '▸ Ver detalles'}
                                </button>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="px-3 pb-3 pt-1 border-t border-[#1F2937]/60 space-y-2 bg-[#13131A]">
                                {exec.source_sha256 && (
                                  <div className="text-[10px] font-mono text-[#9CA3AF] flex items-center gap-1.5">
                                    <span className="text-[#6B7280]">SHA-256:</span>
                                    <span className="text-[#F3F4F6] select-all">{exec.source_sha256}</span>
                                  </div>
                                )}

                                <div className="space-y-1">
                                  <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-mono font-semibold">
                                    Salida Estándar (stdout):
                                  </span>
                                  <div className="bg-[#0A0C10] border border-[#1F2937] p-2.5 text-xs font-mono text-[#F3F4F6] whitespace-pre-wrap max-h-48 overflow-y-auto leading-relaxed">
                                    {exec.stdout ? exec.stdout : <span className="italic text-[#6B7280]">(Sin salida estándar)</span>}
                                  </div>
                                </div>

                                {exec.stderr && (
                                  <div className="space-y-1">
                                    <span className="text-[10px] uppercase tracking-wider text-[#F85149] font-mono font-semibold">
                                      Salida de Error (stderr):
                                    </span>
                                    <div className="bg-[#1C1014] border border-[#F85149]/40 p-2.5 text-xs font-mono text-[#F85149] whitespace-pre-wrap max-h-36 overflow-y-auto leading-relaxed">
                                      {exec.stderr}
                                    </div>
                                  </div>
                                )}

                                {exec.error_message && (
                                  <div className="p-2 bg-[#1C1014] border border-[#F85149]/40 text-[#F85149] text-xs font-mono">
                                    Mensaje de error: {exec.error_message}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="sandbox" className="mt-0 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
              <ScrollArea className="h-full flex-1 min-h-0">
                <div className="bg-[#1C1C24] border border-[#1F2937] p-6 space-y-6">
                  {/* Header & Security Notice */}
                  <div className="flex items-center justify-between border-b border-[#1F2937] pb-3 flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold terminal-font text-[#F3F4F6]">
                          Sandbox Aislado de Ejecución Real
                        </h3>
                        <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} label="ISOLATED RUNNER" />
                      </div>
                      <p className="text-xs body-font text-[#9CA3AF] mt-1">
                        Ejecución aislada sin acceso de red.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 text-xs font-mono bg-[#13131A] text-[#00E5FF] border border-[#00E5FF]/30">
                        Runtime: Python 3.11
                      </span>
                    </div>
                  </div>

                  {/* Code Input & Controls */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold terminal-font text-[#F3F4F6]">
                        Código Python a Ejecutar:
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#9CA3AF] terminal-font">Timeout (1-30s):</span>
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={timeoutSeconds}
                          onChange={(e) => setTimeoutSeconds(e.target.value)}
                          className="w-16 bg-[#0A0C10] border border-[#1F2937] text-xs font-mono text-[#F3F4F6] px-2 py-1 text-center"
                        />
                      </div>
                    </div>

                    <textarea
                      value={sourceCode}
                      onChange={(e) => setSourceCode(e.target.value)}
                      rows={6}
                      className="w-full bg-[#0A0C10] border border-[#1F2937] text-[#F3F4F6] font-mono text-xs p-3 leading-relaxed focus:outline-none focus:border-[#00E5FF]"
                      placeholder="print('Hello from VASTEON Isolated Sandbox')"
                    />

                    {pollingError && (
                      <div className="p-3 bg-[#1C1014] border border-[#F85149]/40 text-[#F85149] text-xs font-mono">
                        {pollingError}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3 pt-2">
                      <Button
                        data-testid={DASHBOARD.sandboxRunBtn}
                        onClick={handleExecuteSandbox}
                        disabled={!project || running}
                        className="bg-[#00E5FF] text-[#0A0C10] hover:bg-[#00E5FF]/90 terminal-font text-xs font-bold"
                      >
                        {running ? (
                          <>
                            <ArrowsClockwise size={14} className="mr-2 animate-spin" />
                            Ejecutando en Sandbox...
                          </>
                        ) : (
                          <>
                            <PlayCircle size={14} className="mr-2" />
                            Ejecutar en Sandbox
                          </>
                        )}
                      </Button>
                      <Button
                        data-testid={DASHBOARD.approveResultBtn}
                        onClick={onApproveResult}
                        disabled={!project || project.human_approved}
                        className="bg-[#10B981] text-[#0A0C10] hover:bg-[#10B981]/90 terminal-font text-xs font-bold"
                      >
                        <CheckCircle size={14} className="mr-2" />
                        {project?.human_approved ? 'Aprobado por Usuario' : 'Aprobar Resultado (Confirmación Real)'}
                      </Button>
                      <Button
                        data-testid={DASHBOARD.archiveBtn}
                        onClick={onArchiveProject}
                        disabled={!project || exporting}
                        className="bg-[#D97706] text-[#0A0C10] hover:bg-[#D97706]/90 terminal-font text-xs font-bold"
                      >
                        <DownloadSimple size={14} className="mr-2" />
                        {exporting ? 'Exportando ZIP...' : 'Export Project (ZIP Real)'}
                      </Button>
                    </div>
                  </div>

                  {/* Active Telemetry Viewer */}
                  {activeExecution && (
                    <div className="space-y-3 border-t border-[#1F2937] pt-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <h4 className="text-xs font-bold terminal-font text-[#F3F4F6] uppercase tracking-wider">
                            Telemetría de Ejecución
                          </h4>
                          {getStatusBadge(activeExecution.status)}
                        </div>
                        <div className="flex items-center gap-4 text-xs font-mono">
                          <div>
                            <span className="text-[#9CA3AF]">Exit Code: </span>
                            <span className="text-[#F3F4F6] font-bold">
                              {activeExecution.exit_code !== null && activeExecution.exit_code !== undefined
                                ? activeExecution.exit_code
                                : '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-[#9CA3AF]">Duración: </span>
                            <span className="text-[#00E5FF] font-bold">
                              {activeExecution.duration_ms !== null && activeExecution.duration_ms !== undefined
                                ? `${activeExecution.duration_ms} ms`
                                : '-'}
                            </span>
                          </div>
                          <div className="text-[10px] text-[#9CA3AF]" title={activeExecution.execution_id}>
                            ID: {activeExecution.execution_id ? activeExecution.execution_id.slice(0, 8) : '-'}...
                          </div>
                        </div>
                      </div>

                      {/* stdout display */}
                      <div className="space-y-1">
                        <span className="text-[11px] text-[#9CA3AF] terminal-font">Standard Output (stdout):</span>
                        <div className="bg-[#0A0C10] border border-[#1F2937] p-3 text-xs font-mono text-[#F3F4F6] whitespace-pre-wrap max-h-48 overflow-y-auto">
                          {activeExecution.stdout ? activeExecution.stdout : <span className="italic text-[#9CA3AF]">(Sin salida estándar)</span>}
                        </div>
                      </div>

                      {/* stderr display if present */}
                      {activeExecution.stderr && (
                        <div className="space-y-1">
                          <span className="text-[11px] text-[#F85149] terminal-font">Standard Error (stderr):</span>
                          <div className="bg-[#1C1014] border border-[#F85149]/40 p-3 text-xs font-mono text-[#F85149] whitespace-pre-wrap max-h-36 overflow-y-auto">
                            {activeExecution.stderr}
                          </div>
                        </div>
                      )}

                      {/* error message if present */}
                      {activeExecution.error_message && (
                        <div className="p-2 bg-[#1C1014] border border-[#F85149]/40 text-[#F85149] text-xs font-mono">
                          Error: {activeExecution.error_message}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Execution History from Backend */}
                  {projectExecutions.length > 0 && (
                    <div className="space-y-2 border-t border-[#1F2937] pt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold terminal-font text-[#9CA3AF] uppercase tracking-wider">
                          Historial de Ejecuciones (Persistidas en MongoDB)
                        </h4>
                        <span className="text-[10px] font-mono text-[#9CA3AF]">{projectExecutions.length} registro(s)</span>
                      </div>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {projectExecutions.map((exec) => (
                          <div
                            key={exec.execution_id}
                            onClick={() => setActiveExecution(exec)}
                            className={`p-2 bg-[#13131A] border cursor-pointer flex items-center justify-between text-xs font-mono transition-colors ${
                              activeExecution?.execution_id === exec.execution_id
                                ? 'border-[#00E5FF] text-[#F3F4F6]'
                                : 'border-[#1F2937] text-[#9CA3AF] hover:border-[#9CA3AF]'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-[#9CA3AF]">
                                {exec.execution_id.slice(0, 8)}...
                              </span>
                              <span className="font-bold">{exec.status.toUpperCase()}</span>
                            </div>
                            <div className="flex items-center gap-3 text-[11px]">
                              <span>Exit: {exec.exit_code !== null && exec.exit_code !== undefined ? exec.exit_code : '-'}</span>
                              <span className="text-[#00E5FF]">
                                {exec.duration_ms ? `${exec.duration_ms}ms` : '-'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default InteroperabilityWorkspace;
